import { apiClient } from '../axios-instance'
import type { AxiosResponse } from 'axios'

export interface User {
  id: number
  user_id: number
  full_name: string
  email: string | null
  mobile: string
  role: 'customer' | 'merchant' | 'admin'
  status: 'active' | 'suspended' | 'blocked'
  profile_image: string | null
  email_verified_at: string | null
  phone_verified_at: string | null
  created_at: string
  updated_at: string
}

export interface PaginatedResponse<T> {
  data: T[]
  meta: {
    current_page: number
    from: number
    last_page: number
    per_page: number
    to: number
    total: number
  }
  links: {
    first: string
    last: string
    prev: string | null
    next: string | null
  }
}

export interface UserFilters {
  search?: string
  role?: string
  status?: string
  email_verified?: boolean
  phone_verified?: boolean
  created_from?: string
  created_to?: string
  page?: number
  per_page?: number
  sort_by?: string
  sort_order?: 'asc' | 'desc'
}

export interface CreateUserRequest {
  user_id: number
  full_name: string
  email?: string
  mobile: string
  role: 'customer' | 'merchant' | 'admin'
  status?: 'active' | 'suspended' | 'blocked'
}

export interface UpdateUserRequest {
  full_name?: string
  email?: string
  status?: 'active' | 'suspended' | 'blocked'
}

/**
 * User Service
 * Manages user CRUD operations
 */
export const userService = {
  /**
   * Get paginated list of users
   */
  getUsers: async (filters?: UserFilters): Promise<PaginatedResponse<User>> => {
    const response: AxiosResponse<PaginatedResponse<User>> = await apiClient.get(
      '/users',
      { params: filters }
    )
    return response.data
  },

  /**
   * Get user by ID
   */
  getUserById: async (id: number): Promise<User> => {
    const response: AxiosResponse<{ success: boolean; data: User }> = await apiClient.get(`/users/${id}`)
    return response.data.data
  },

  /**
   * Create new user
   */
  createUser: async (data: CreateUserRequest): Promise<User> => {
    const response: AxiosResponse<{ success: boolean; data: User }> = await apiClient.post('/users', data)
    return response.data.data
  },

  /**
   * Update user
   */
  updateUser: async (id: number, data: UpdateUserRequest): Promise<User> => {
    const response: AxiosResponse<{ success: boolean; data: User }> = await apiClient.put(`/users/${id}`, data)
    return response.data.data
  },

  /**
   * Delete user
   */
  deleteUser: async (id: number): Promise<void> => {
    await apiClient.delete(`/users/${id}`)
  },

  /**
   * Suspend user
   */
  suspendUser: async (id: number, reason?: string): Promise<User> => {
    const response: AxiosResponse<{ success: boolean; data: User }> = await apiClient.post(
      `/users/${id}/suspend`,
      { reason }
    )
    return response.data.data
  },

  /**
   * Unsuspend user
   */
  unsuspendUser: async (id: number): Promise<User> => {
    const response: AxiosResponse<{ success: boolean; data: User }> = await apiClient.post(`/users/${id}/unsuspend`)
    return response.data.data
  },

  /**
   * Ban user
   */
  banUser: async (id: number, reason?: string): Promise<User> => {
    const response: AxiosResponse<{ success: boolean; data: User }> = await apiClient.post(
      `/users/${id}/ban`,
      { reason }
    )
    return response.data.data
  },

  /**
   * Verify user email
   */
  verifyEmail: async (id: number): Promise<User> => {
    const response: AxiosResponse<{ success: boolean; data: User }> = await apiClient.post(`/users/${id}/verify-email`)
    return response.data.data
  },

  /**
   * Verify user phone
   */
  verifyPhone: async (id: number): Promise<User> => {
    const response: AxiosResponse<{ success: boolean; data: User }> = await apiClient.post(`/users/${id}/verify-phone`)
    return response.data.data
  },

  /**
   * Bulk delete users
   */
  bulkDelete: async (ids: number[]): Promise<void> => {
    await apiClient.post('/users/bulk-delete', { ids })
  },

  /**
   * Bulk update user status
   */
  bulkUpdateStatus: async (ids: number[], status: User['status']): Promise<void> => {
    await apiClient.post('/users/bulk-update-status', { ids, status })
  },

  /**
   * Export users to CSV
   */
  exportUsers: async (filters?: UserFilters): Promise<Blob> => {
    const response = await apiClient.get('/users/export', {
      params: filters,
      responseType: 'blob',
    })
    return response.data
  },
}
